import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator'
import { isBefore, subDays, parseISO, isValid } from 'date-fns'

import Text from '../../../ui/Text'
import { notifyError, notifySuccess } from '../../features/notificationsManager/Notify'
import {
    ChargingSession,
    useChargePointGetChargingSessionsWithCountQuery,
    useChargingSessionDeleteChargingSessionMutation,
    useChargePointGetQuery,
} from '../../redux/api/web-api'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import isAura from '../../../lib/isAura'
import useText from '../../hooks/useText'
import { Table } from '../../uikit/Table'
import { dateLocalize } from '../../../lib/date-localize'
import { confirm } from '../../uikit/ConfirmDialog'

import { ConsumptionGraphDialog } from './dialogs/consumption-graph-dialog'

export const SIcon = styled.div`
    cursor: pointer;
    margin-left: 10px;
    color: ${({ theme }) => theme.brandColors.CA_Blue};
`

const SFilterLabel = styled.label`
    font-size: 13px;
    margin-top: 5px;
`

const SFilter = styled.span`
    display: flex;
    flex-direction: row;
    gap: 4px;
    justify-content: flex-end;
    margin: 10px 0;

    @media (max-width: 1200px) {
        justify-content: flex-start;
    }
`

export type ChargepointLatestChargingSessionsWidgetProps = {
    chargePointId: string
    useFilter?: boolean
    deleteEnabled?: boolean
}

export function ChargepointLatestChargingSessionsWidget({
    chargePointId,
    deleteEnabled = false,
    useFilter = false,
}: ChargepointLatestChargingSessionsWidgetProps): React.ReactElement {
    const text = useText()
    const [currentPage, setCurrentPage] = useState(0)
    const [filter, setFilter] = useState(useFilter)
    const [isConsumptionGraphDialogVisible, setIsConsumptionGraphDialogVisible] = useState(false)
    const [currentSession, setCurrentSession] = useState<ChargingSession>()
    const [removeSession] = useChargingSessionDeleteChargingSessionMutation()
    const noOfItems = 10

    const adaptConnectorNaming = useAdaptConnectorNaming()

    const { data: chargePoint } = useChargePointGetQuery({ chargePointId })

    const filterSubmit = () => {
        if (!filter) {
            setFilter(true)
            setCurrentPage(0)
        } else {
            setFilter(false)
            setCurrentPage(0)
        }
    }

    const openConsumptionGraphDialog = (session: ChargingSession) => {
        setCurrentSession(session)
        setIsConsumptionGraphDialogVisible(true)
    }

    const closeConsumptionGraphDialog = useCallback(() => {
        setIsConsumptionGraphDialogVisible(false)
    }, [])

    const {
        data: sessionData,
        isLoading: sessionLoading,
        refetch,
    } = useChargePointGetChargingSessionsWithCountQuery({
        chargePointId: chargePointId ?? location.hash.split('/')[2],
        filterZero: filter,
        noOfItems: noOfItems,
        offset: currentPage * noOfItems,
    })

    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setCurrentPage(event.page)
    }

    const handleRemoveSession = async (id?: number) => {
        if (!id) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('lblConfirmDeletingChargingSession'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeSession({ id }).unwrap()
            notifySuccess(text('lblLatestChargingSessionsRemoveSession'))
            refetch()
        } catch (e) {
            notifyError(text('lblLatestChargingSessionRemoveError'))
        }
    }

    const isOlderThanDays = (sessionDateString: string | undefined, days: number): boolean => {
        if (!sessionDateString) return false

        const sessionDate = parseISO(sessionDateString)
        return isValid(sessionDate) && isBefore(sessionDate, subDays(Date.now(), days))
    }

    return (
        <>
            {chargePoint && (
                <ConsumptionGraphDialog
                    visible={isConsumptionGraphDialogVisible}
                    onHide={closeConsumptionGraphDialog}
                    session={currentSession}
                    chargePoint={chargePoint}
                    connectorPhaseRotation={chargePoint.connectorPhaseRotation ?? undefined}
                />
            )}

            {useFilter && (
                <SFilter>
                    <input
                        className="checkbox-black padding-4"
                        type="checkbox"
                        defaultChecked={!filter}
                        onChange={filterSubmit}
                        id="filter"
                        data-testid="filter"
                    />
                    <SFilterLabel htmlFor="filter">
                        <Text code="lblShowEmptyChargingSessionsField"></Text>{' '}
                    </SFilterLabel>
                </SFilter>
            )}

            <Table
                value={sessionData?.sessions ?? []}
                loading={sessionLoading}
                emptyMessage={text('msgNoChargingSessionsFound')}
                data-testid="charging-sessions"
            >
                {sessionData && sessionData?.sessions && isAura(sessionData && sessionData?.sessions[0]) ? (
                    <Column
                        field="socket"
                        header={text('lblSocket')}
                        body={(session: ChargingSession) =>
                            session.connectorId === 1 ? (
                                <>
                                    <i className="pi pi-circle-fill ml-2"></i>
                                    <i className="pi pi-circle ml-2"></i>
                                </>
                            ) : (
                                <>
                                    <i className="pi pi-circle ml-2"></i>
                                    <i className="pi pi-circle-fill ml-2"></i>
                                </>
                            )
                        }
                    ></Column>
                ) : (
                    <Column
                        field="socket"
                        header={text('lblSocket')}
                        body={(session: ChargingSession) =>
                            session.connectorFunctionType === 'Charger' ? (
                                <i className="pi pi-car ml-2"></i>
                            ) : (
                                <span
                                    className="fa icon-socket ml-2"
                                    style={{ fontSize: 16 }}
                                    title={adaptConnectorNaming(session?.connectorName)}
                                />
                            )
                        }
                    ></Column>
                )}

                <Column
                    field="startTime"
                    header={text('lblStart')}
                    body={(session: ChargingSession) => dateLocalize(session.startTime)}
                ></Column>
                <Column
                    field="startTime"
                    header={text('lblEnd')}
                    body={(session: ChargingSession) => dateLocalize(session.endTime)}
                ></Column>
                <Column
                    field="Kwh"
                    header={text('lblKWh')}
                    body={(session: ChargingSession) => session?.totalConsumptionKwh?.toFixed(3)}
                ></Column>

                <Column
                    field="consumptionGraph"
                    body={(session: ChargingSession) => {
                        const isOlderThanFourWeeks = isOlderThanDays(session.startTime, 28)

                        return (
                            <div className="flex justify-content-end gap-3">
                                <Button
                                    outlined
                                    size="small"
                                    severity="info"
                                    icon="pi pi-chart-line"
                                    title={text('lblViewConsumptionGraph')}
                                    data-testid={`graph-icon-button-${session.id}`}
                                    onClick={() => openConsumptionGraphDialog(session)}
                                    style={{ visibility: isOlderThanFourWeeks ? 'hidden' : 'visible' }}
                                />
                                <Button
                                    size="small"
                                    icon="pi pi-trash"
                                    data-testid={`btn-delete-${session.id}`}
                                    rounded
                                    disabled={!deleteEnabled}
                                    outlined
                                    title={text('lblRemoveChargingSession')}
                                    severity="danger"
                                    onClick={() => {
                                        handleRemoveSession(session?.id)
                                    }}
                                />
                            </div>
                        )
                    }}
                ></Column>
            </Table>
            <Paginator
                data-testid="pagination"
                rows={noOfItems}
                first={currentPage * noOfItems}
                totalRecords={sessionData?.totalCount}
                onPageChange={onPageChange}
            />
        </>
    )
}
